import type {AfriexBusinessLog} from '@/types';

type DetailRowProps = {
  label: string;
  value: unknown;
  isBreakWords?: boolean;
  dataCy?: string;
};

/**
 * `value` covers everything a log's `meta` can hold (numbers, strings, the
 * `admin` sub-object's fields), so this only trusts the types whose default
 * stringification is meaningful. A genuine object/array renders as JSON
 * rather than the uninformative `[object Object]`.
 */
const toDisplayString = (value: unknown): string => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  return JSON.stringify(value);
};

const DetailRow = ({
  label,
  value,
  isBreakWords = false,
  dataCy,
}: DetailRowProps) => {
  const display = value != null ? toDisplayString(value) : '';
  return (
    <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
      <p className="text-slate-500 text-sm dark:text-slate-100 shrink-0 whitespace-nowrap">
        {label}
      </p>
      {/* `anywhere`, not `break-word` (Tailwind's `break-words`): only
          `anywhere` lets a long unbroken token (an id, a URL, a stack frame)
          lower the cell's minimum width, so it cannot widen the table around
          the expanded details. */}
      <p
        className={`text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 min-w-0 text-right ${
          isBreakWords
            ? '[overflow-wrap:anywhere] whitespace-normal text-wrap'
            : 'whitespace-nowrap'
        }`}
        title={display}
        data-cy={dataCy}
      >
        {display !== '' ? display : '—'}
      </p>
    </div>
  );
};

const MerchantLogsSummary = ({item}: {item: AfriexBusinessLog}) => {
  const meta = item?.meta ?? {};
  const admin =
    meta.admin && typeof meta.admin === 'object'
      ? (meta.admin as Record<string, unknown>)
      : {};
  const tags = item?.tags?.length ? item.tags.join(', ') : undefined;

  return (
    <div
      className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5"
      data-cy="merchant-logs-summary"
    >
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-xl mr-3">Log Details</h2>
      </div>

      <div>
        <DetailRow
          label="Status Code"
          value={meta.statusCode}
          dataCy="merchant-logs-summary-status-code"
        />
        <DetailRow
          label="URL"
          value={meta.url}
          isBreakWords
          dataCy="merchant-logs-summary-url"
        />
        <DetailRow
          label="HTTP Method"
          value={meta.http_method}
          dataCy="merchant-logs-summary-http-method"
        />
        <DetailRow
          label="IP Address"
          value={meta.ip}
          isBreakWords
          dataCy="merchant-logs-summary-ip"
        />
        <DetailRow
          label="User Agent"
          value={meta.userAgent}
          isBreakWords
          dataCy="merchant-logs-summary-user-agent"
        />
        {admin?.name != null && (
          <DetailRow
            label="Admin Name"
            value={admin.name}
            dataCy="merchant-logs-summary-admin-name"
          />
        )}
        {admin?.role != null && (
          <DetailRow
            label="Admin Role"
            value={admin.role}
            dataCy="merchant-logs-summary-admin-role"
          />
        )}
        <DetailRow
          label="Message"
          value={item?.message}
          isBreakWords
          dataCy="merchant-logs-summary-message"
        />
        <DetailRow
          label="Tags"
          value={tags}
          isBreakWords
          dataCy="merchant-logs-summary-tags"
        />
      </div>
    </div>
  );
};

export default MerchantLogsSummary;
